import React, {useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import Plyr from "plyr-react";
import "plyr-react/plyr.css";
import ReactMarkdown from "react-markdown";

const PlayerPage = () => {
    const {name, season} = useParams();
    const [audio, setAudio] = useState("original");
    const [title, setTitle] = useState("");
    const [episodes, setEpisodes] = useState([]);
    const [currentEpisode, setCurrentEpisode] = useState("");
    const [episodeInfo, setEpisodeInfo] = useState({title: "", description: ""});
    const [seasonData, setSeasonData] = useState(null);
    const navigate = useNavigate();

    const playerRef = useRef(null);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (!playerRef.current) return;
            const player = playerRef.current.plyr;

            switch (event.key) {
                case "f":
                    player.fullscreen.toggle();
                    break;
                case " ":
                    event.preventDefault(); // предотвращает скролл страницы при нажатии пробела
                    if (player.playing) {
                        player.pause();
                    } else {
                        player.play();
                    }
                    break;
                case "ArrowLeft":
                    player.currentTime = Math.max(player.currentTime - 10, 0); // перемотка назад
                    break;
                case "ArrowRight":
                    player.currentTime = Math.min(player.currentTime + 10, player.duration); // перемотка вперёд
                    break;
                case "ArrowUp":
                    player.volume = Math.min(player.volume + 0.1, 1); // громкость вверх
                    break;
                case "ArrowDown":
                    player.volume = Math.max(player.volume - 0.1, 0); // громкость вниз
                    break;
                default:
                    break;
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_STORAGE_URL}/info.json`);
                const data = await response.json();
                const show = data.find((show) => show.name === name);
                if (show) {
                    setTitle(show.title);
                    const seasonData = show.series[season];
                    if (seasonData) {
                        setSeasonData(seasonData);
                        setEpisodes(seasonData.series);

                        // Получаем текущую серию из localStorage или берём первую
                        const lastWatched = localStorage.getItem(`lastWatched-${name}-${season}`);
                        const initialEpisode = lastWatched || seasonData.series[0];
                        setCurrentEpisode(initialEpisode);

                        // Устанавливаем данные о текущей серии
                        const initialEpisodeInfo = seasonData.seriesDescription[initialEpisode];
                        if (initialEpisodeInfo) {
                            setEpisodeInfo({
                                title: initialEpisodeInfo.title,
                                description: initialEpisodeInfo.description,
                            });
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching show data:", error);
            }
        };
        fetchData();
    }, [name, season]);

    useEffect(() => {
        // Сохраняем текущую серию в localStorage
        if (currentEpisode) {
            localStorage.setItem(`lastWatched-${name}-${season}`, currentEpisode);
        }
    }, [name, season, currentEpisode]);

    const handleEpisodeClick = (episode) => {
        setCurrentEpisode(episode);

        if (seasonData && seasonData.seriesDescription[episode]) {
            const newEpisodeInfo = seasonData.seriesDescription[episode];
            setEpisodeInfo({
                title: newEpisodeInfo.title,
                description: newEpisodeInfo.description,
            });
        }
    };

    const videoSrc = {
        type: "video",
        sources: [
            {
                src: `${process.env.REACT_APP_STORAGE_URL}/${name}/${season}/${currentEpisode}/video/${audio}/1080/master.mp4`,
                type: "video/mp4",
                size: 1080,
            },
            {
                src: `${process.env.REACT_APP_STORAGE_URL}/${name}/${season}/${currentEpisode}/video/${audio}/720/master.mp4`,
                type: "video/mp4",
                size: 720,
            },
            {
                src: `${process.env.REACT_APP_STORAGE_URL}/${name}/${season}/${currentEpisode}/video/${audio}/480/master.mp4`,
                type: "video/mp4",
                size: 480,
            },
        ],
        tracks: [
            {
                kind: "subtitles",
                label: "English",
                src: `${process.env.REACT_APP_STORAGE_URL}/${name}/${season}/${currentEpisode}/subs/english.vtt`,
                srclang: "en",
                default: true,
            },
            {
                kind: "subtitles",
                label: "Русский",
                src: `${process.env.REACT_APP_STORAGE_URL}/${name}/${season}/${currentEpisode}/subs/russian.vtt`,
                srclang: "ru",
                default: false,
            },
        ],
    };

    function iOS() {
        return /iPad|iPhone|iPod/.test(navigator.userAgent);
    }

    return (
        <div className="padding-container">
            <div className='container'>
                <h1 onClick={() => {
                    navigate(`/seasons/${name}`)
                }}
                    className="player-series-title">{title}</h1>
                <h2>Сезон {season.replace("s", "")} Серия {currentEpisode}</h2>

                <div className="audio-label">
                    <label>
                        Озвучка:{" "}
                        <select value={audio} onChange={(e) => setAudio(e.target.value)}>
                            {Object.entries({
                                cube: "Кубик в Кубе",
                                goblin: "Гоблин",
                                lostfilm: "LostFilm",
                                original: "Оригинал",
                            }).map(([key, displayName]) => (
                                <option key={key} value={key}>
                                    {displayName}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
                <div className="player-container">
                    <Plyr
                        crossOrigin="anonymous"
                        options={{
                            fullscreen: {
                                iosNative: iOS(),
                            },
                            controls: [
                                "play-large",
                                "play",
                                "progress",
                                "current-time",
                                "mute",
                                "volume",
                                "captions",
                                "settings",
                                "pip",
                                "fullscreen",
                            ],
                            captions: {active: true, language: "auto", update: true},
                        }}
                        source={videoSrc}
                        ref={playerRef}
                    />
                </div>
                <div className="episodes-container">
                    {episodes.map((ep) => (
                        <div
                            className="series-button"
                            key={ep}
                            onClick={() => handleEpisodeClick(ep)}
                            style={{margin: "5px", padding: "10px"}}
                        >
                            Серия {ep}
                        </div>
                    ))}
                </div>
                <h2>{episodeInfo.title}</h2>
                <ReactMarkdown className="markdown">{episodeInfo.description}</ReactMarkdown>
            </div>
        </div>
    );
};

export default PlayerPage;
